import React from "react";
import { Link } from "react-router-dom";
import LegalDocumentPage from "@/components/LegalDocumentPage";

const LegalList = ({ children, ordered = false }) => {
  const Element = ordered ? "ol" : "ul";
  return <Element className={`ml-5 space-y-2 ${ordered ? "list-decimal" : "list-disc"}`}>{children}</Element>;
};

const DMCA_SECTIONS = [
  { heading: "Effective Date", content: "July 31, 2026" },
  {
    heading: "Copyright Policy",
    content: (
      <>
        ShowMeLive respects intellectual-property rights and expects users to upload or broadcast only
        content they own or are authorized to use. We may remove or disable access to content, preserve
        evidence, notify the affected user, restrict livestreaming or account features, and terminate
        repeat infringers in appropriate circumstances. This policy is intended to implement the notice
        and counter-notice procedures in 17 U.S.C. § 512 and does not constitute legal advice.
      </>
    ),
  },
  {
    heading: "Before Sending a Notice",
    content: "Consider whether the material is authorized by the copyright owner or protected by fair use, license, public-domain status, or another legal exception. Knowingly making a material misrepresentation in a notice or counter-notice may create liability under 17 U.S.C. § 512(f).",
  },
  {
    heading: "DMCA Takedown Notice",
    content: (
      <>
        A copyright owner or authorized agent may email a written notice to{" "}
        <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:legal@showmelive.app">legal@showmelive.app</a>
        {" "}with the subject "DMCA Notice." To be effective, include:
        <LegalList ordered>
          <li>Your physical or electronic signature.</li>
          <li>Identification of the copyrighted work claimed to be infringed, or a representative list if one notice covers multiple works.</li>
          <li>Identification of the allegedly infringing material and information reasonably sufficient for us to locate it, including the ShowMeLive event, account, URL, date, and timestamp where available.</li>
          <li>Your name, mailing address, telephone number, and email address.</li>
          <li>A statement that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law.</li>
          <li>A statement that the notice is accurate and, under penalty of perjury, that you are the copyright owner or authorized to act for the owner.</li>
        </LegalList>
        Incomplete notices may delay our response. We may send the notice, including contact information,
        to the user who posted the material or to third parties involved in reviewing the claim.
      </>
    ),
  },
  {
    heading: "Our Response",
    content: "When we receive a substantially compliant notice, we may act expeditiously to remove or disable access to the identified material. We will take reasonable steps to notify the affected user and may record the claim for repeat-infringer enforcement. We may request additional information, decline notices that do not identify protectable copyrighted material or the content at issue, and restore content when permitted by law.",
  },
  {
    heading: "DMCA Counter-Notice",
    content: (
      <>
        If your content was removed or disabled because of a DMCA notice and you believe that occurred
        through mistake or misidentification, email a written counter-notice to{" "}
        <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:legal@showmelive.app">legal@showmelive.app</a>
        {" "}with the subject "DMCA Counter-Notice." Include:
        <LegalList ordered>
          <li>Your physical or electronic signature.</li>
          <li>Identification of the material removed or disabled and the location where it appeared before removal.</li>
          <li>A statement under penalty of perjury that you have a good-faith belief the material was removed or disabled because of mistake or misidentification.</li>
          <li>Your name, mailing address, telephone number, and email address.</li>
          <li>A statement that you consent to the jurisdiction of the appropriate United States federal district court for your address, or if outside the United States, a federal district where ShowMeLive may be found, and that you will accept service of process from the person who submitted the notice or that person's agent.</li>
        </LegalList>
      </>
    ),
  },
  {
    heading: "Restoration After a Counter-Notice",
    content: "We may forward a valid counter-notice to the complaining party. Unless that party notifies us that it filed a court action seeking to restrain the alleged infringement, we may restore the material no sooner than 10 and no later than 14 business days after receiving the counter-notice, as provided by applicable law.",
  },
  {
    heading: "Repeat Infringers and Other Remedies",
    content: "We may terminate accounts of repeat infringers in appropriate circumstances and may address infringement outside the DMCA process under our Terms, contracts, or other applicable law. Withdrawal of a complaint does not require us to restore content or reverse separate policy enforcement.",
  },
  {
    heading: "Copyright Contact and Designated Agent",
    content: (
      <>
        DMCA Agent / Legal Department<br />
        Emerging Technology LLC, operator of ShowMeLive<br />
        Jackson County, Missouri, United States<br />
        Email: <a className="font-semibold text-blue-400 hover:text-blue-300" href="mailto:legal@showmelive.app">legal@showmelive.app</a><br /><br />
        The service provider's public agent details should be kept identical to its current designation
        in the U.S. Copyright Office directory. A claimant who needs a complete mailing address or phone
        number should consult that official directory or request it at the email above before submitting
        a notice. General privacy requests belong under the{" "}
        <Link className="font-semibold text-blue-400 hover:text-blue-300" to="/privacy-policy">Privacy Policy</Link>.
      </>
    ),
  },
];

const CopyrightDmca = () => (
  <LegalDocumentPage
    title="ShowMeLive Copyright and DMCA Policy"
    sections={DMCA_SECTIONS}
    testId="copyright-dmca-page"
  />
);

export default CopyrightDmca;
